import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { Calendar } from "lucide-react";
import { API_METHODS } from "../../constants";
import useApi from "../../hooks/useApi";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";

const formatDateRange = (start, end) => {
  const s = new Date(start).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  const e = new Date(end).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  return start === end ? e : `${s} – ${e}`;
};

const EventCard = ({ event, onClick }) => (
  <div
    onClick={onClick}
    style={{
      borderRadius: 16,
      overflow: "hidden",
      background: "#fff",
      border: "1px solid #fed7aa",
      boxShadow: "0 4px 16px rgba(249,115,22,0.10)",
      cursor: "pointer",
      transition: "transform 0.2s, box-shadow 0.2s",
      display: "flex",
      flexDirection: "column",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-4px)";
      e.currentTarget.style.boxShadow = "0 12px 28px rgba(249,115,22,0.18)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 4px 16px rgba(249,115,22,0.10)";
    }}
  >
    {/* Image */}
    <div style={{ position: "relative", height: 180, background: "#fff3e0", flexShrink: 0 }}>
      {event.cover_image ? (
        <img
          src={event.cover_image}
          alt={event.heading}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "linear-gradient(135deg, #f97316, #ef4444)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 48,
          }}
        >
          🙏
        </div>
      )}
      {/* Date pill */}
      <div
        style={{
          position: "absolute",
          top: 12,
          left: 12,
          background: "rgba(0,0,0,0.55)",
          color: "#fff",
          fontSize: 11,
          fontWeight: 600,
          padding: "4px 12px",
          borderRadius: 20,
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        <Calendar size={11} />
        {formatDateRange(event.start_date, event.end_date)}
      </div>
    </div>

    {/* Content */}
    <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", flex: 1 }}>
      <div
        style={{
          fontWeight: 700,
          fontSize: 15,
          color: "#1f2937",
          lineHeight: 1.4,
          marginBottom: 6,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {event.heading}
      </div>
      {event.sub_heading && (
        <div
          style={{
            fontSize: 13,
            color: "#6b7280",
            marginBottom: 12,
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {event.sub_heading}
        </div>
      )}

      <div style={{ marginTop: "auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {event.starting_price ? (
          <div style={{ fontSize: 12, color: "#9ca3af" }}>
            from{" "}
            <span style={{ fontWeight: 700, fontSize: 16, color: "#f97316" }}>
              ₹{event.starting_price}
            </span>
          </div>
        ) : <div />}
        <button
          style={{
            background: "linear-gradient(to right, #f97316, #ef4444)",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "9px 20px",
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Book Now
        </button>
      </div>
    </div>
  </div>
);

const EventsPage = () => {
  const navigate = useNavigate();
  const [eventsResponse, fetchEvents] = useApi("/user/events", null, API_METHODS.GET);

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const events = eventsResponse?.data || [];
  const isLoading = !eventsResponse || eventsResponse.isLoading === null || eventsResponse.isLoading;

  return (
    <>
      <HeaderComponent />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
        {/* Page Header */}
        <div
          style={{
            background: "linear-gradient(to right, #f97316, #ef4444)",
            padding: "48px 24px 40px",
            textAlign: "center",
          }}
        >
          <p
            style={{
              color: "rgba(255,255,255,0.85)",
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Special Events
          </p>
          <h1 style={{ color: "#fff", fontWeight: 800, fontSize: 34, margin: 0 }}>
            Upcoming Pujas & Events
          </h1>
          <p style={{ color: "rgba(255,255,255,0.8)", marginTop: 10, fontSize: 15 }}>
            A spiritual ritual to seek blessings, and positive energy
          </p>
        </div>

        <div
          className="container mx-auto"
          style={{ padding: "48px 24px 64px", maxWidth: 1100 }}
        >
          {isLoading && (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <Spin size="large" />
            </div>
          )}

          {!isLoading && events.length === 0 && (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🙏</div>
              <h3 style={{ fontWeight: 700, color: "#374151", fontSize: 20, marginBottom: 8 }}>
                No upcoming events right now
              </h3>
              <p style={{ color: "#6b7280", marginBottom: 28 }}>
                Check back soon for upcoming special pujas and events.
              </p>
              <button
                onClick={() => navigate("/services")}
                style={{
                  background: "linear-gradient(to right, #f97316, #ef4444)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "11px 28px",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Browse All Services
              </button>
            </div>
          )}

          {!isLoading && events.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 24,
              }}
            >
              {events.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onClick={() => navigate(`/event/${event.slug}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default EventsPage;
